import { useState } from 'react'
import type { UnitType, UnitStatus } from '@territory-nexus/shared'
import { STATUS_CONFIG, UNIT_META } from '@territory-nexus/shared'
import clsx from 'clsx'

// Individual sprite paths
const UNIT_SPRITES: Record<UnitType, string> = {
  scout_drone: '/assets/units/unit-scout-drone.png',
  assault_bot: '/assets/units/unit-assault-bot.png',
  heavy_mech: '/assets/units/unit-heavy-mech.png',
  nano_swarm: '/assets/units/unit-nano-swarm.png',
  emp_specialist: '/assets/units/unit-emp-specialist.png',
  titan_mech: '/assets/units/unit-titan-mech.png',
}

// Corrupted AI unit sprite paths (fallback to CSS filter if missing)
const CORRUPTED_SPRITES: Record<UnitType, string> = {
  scout_drone: '/assets/units/corrupted/corrupted-scout-drone.png',
  assault_bot: '/assets/units/corrupted/corrupted-assault-bot.png',
  heavy_mech: '/assets/units/corrupted/corrupted-heavy-mech.png',
  nano_swarm: '/assets/units/corrupted/corrupted-nano-swarm.png',
  emp_specialist: '/assets/units/corrupted/corrupted-emp-specialist.png',
  titan_mech: '/assets/units/corrupted/corrupted-titan-mech.png',
}

interface UnitSpriteProps {
  unitType: UnitType
  level: number
  status: UnitStatus
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showLevel?: boolean
  animate?: boolean
  corrupted?: boolean
  className?: string
}

const SIZES = {
  sm: 40,
  md: 56,
  lg: 80,
  xl: 112,
}

export default function UnitSprite({
  unitType,
  level,
  status,
  size = 'md',
  showLevel = true,
  animate = true,
  corrupted = false,
  className,
}: UnitSpriteProps) {
  const pixelSize = SIZES[size]
  const meta = UNIT_META[unitType]

  // For corrupted units: try corrupted sprite, CSS filter fallback
  const [useCorruptedSprite, setUseCorruptedSprite] = useState(corrupted)
  const spriteUrl = useCorruptedSprite ? CORRUPTED_SPRITES[unitType] : UNIT_SPRITES[unitType]

  // Level-based glow intensity (1-20 scale mapped to opacity)
  const glowIntensity = Math.min(level / 20, 1)
  const glowColor = corrupted ? 'rgba(168, 85, 247, 0.6)' : STATUS_CONFIG[status].glowColor

  // CSS filter: corrupted fallback (when PNG not found) or normal
  const corruptedFilter = 'brightness(0.85) saturate(1.4) hue-rotate(260deg) drop-shadow(0 0 6px rgba(168,85,247,0.6))'
  const normalBattleFilter = 'brightness(1.15) saturate(1.3) drop-shadow(0 0 4px rgba(239,68,68,0.5))'
  const normalFilter = 'brightness(1.05) saturate(1.25) drop-shadow(0 0 3px rgba(6,182,212,0.25))'

  const imgFilter = corrupted && !useCorruptedSprite
    ? corruptedFilter
    : corrupted
      ? 'brightness(1.05) saturate(1.2) drop-shadow(0 0 5px rgba(168,85,247,0.5))'
      : status === 'in_battle' ? normalBattleFilter : normalFilter

  return (
    <div
      className={clsx(
        'relative inline-flex items-center justify-center',
        animate && status === 'deployed' && !corrupted && 'animate-pulse',
        animate && status === 'in_battle' && 'animate-bounce',
        className
      )}
      style={{ width: pixelSize, height: pixelSize }}
    >
      {/* Glow effect layer */}
      <div
        className="absolute inset-0 rounded-full transition-all duration-300"
        style={{
          background: `radial-gradient(circle, ${glowColor} 0%, transparent 70%)`,
          opacity: corrupted ? 0.4 + glowIntensity * 0.2 : 0.25 + glowIntensity * 0.2,
          transform: `scale(${1.1 + glowIntensity * 0.15})`,
          filter: 'blur(6px)',
        }}
      />

      {/* Scanline sweep for deployed units (not corrupted) */}
      {animate && status === 'deployed' && !corrupted && (
        <div
          className="absolute inset-0 z-[5] pointer-events-none overflow-hidden rounded"
          style={{ opacity: 0.15 }}
        >
          <div
            className="absolute inset-0 animate-scan"
            style={{
              background: 'linear-gradient(transparent 40%, rgba(6, 182, 212, 0.4) 50%, transparent 60%)',
              height: '200%',
            }}
          />
        </div>
      )}

      {/* Corruption pulse overlay for corrupted units */}
      {corrupted && animate && (
        <div
          className="absolute inset-0 z-[5] pointer-events-none rounded-full animate-pulse"
          style={{
            background: 'radial-gradient(circle, rgba(168,85,247,0.15) 0%, transparent 70%)',
          }}
        />
      )}

      {/* Sprite image */}
      <img
        src={spriteUrl}
        alt={corrupted ? `Corrupted ${meta.name}` : meta.name}
        className="relative z-10 object-contain"
        style={{
          width: pixelSize,
          height: pixelSize,
          filter: imgFilter,
        }}
        onError={() => {
          // If corrupted PNG not found, fall back to normal sprite with CSS filter
          if (useCorruptedSprite) setUseCorruptedSprite(false)
        }}
      />

      {/* Level badge */}
      {showLevel && (
        <div
          className={clsx(
            'absolute -bottom-1 -right-1 z-20 rounded-full flex items-center justify-center text-[10px] font-bold',
            corrupted
              ? 'bg-slate-900 border border-purple-500/50 text-purple-400'
              : 'bg-slate-900 border border-cyan-500/50 text-cyan-400',
            !corrupted && level >= 10 && 'border-amber-500/50 text-amber-400',
            !corrupted && level >= 15 && 'border-violet-500/50 text-violet-400',
            !corrupted && level >= 20 && 'border-rose-500/50 text-rose-400 animate-pulse'
          )}
          style={{
            width: Math.max(16, pixelSize * 0.35),
            height: Math.max(16, pixelSize * 0.35),
            fontSize: Math.max(8, pixelSize * 0.2),
          }}
        >
          {level}
        </div>
      )}

      {/* Status indicator (normal units) / Corruption indicator (AI units) */}
      {corrupted ? (
        <div
          className="absolute -top-1 -left-1 z-20 rounded-full bg-purple-500 animate-pulse"
          style={{
            width: Math.max(8, pixelSize * 0.15),
            height: Math.max(8, pixelSize * 0.15),
          }}
        />
      ) : status !== 'idle' && (
        <div
          className={clsx(
            'absolute -top-1 -left-1 z-20 rounded-full',
            status === 'deployed' && STATUS_CONFIG[status].dotColor,
            status === 'in_transit' && `${STATUS_CONFIG[status].dotColor} animate-ping`,
            status === 'in_battle' && `${STATUS_CONFIG[status].dotColor} animate-pulse`
          )}
          style={{
            width: Math.max(8, pixelSize * 0.15),
            height: Math.max(8, pixelSize * 0.15),
          }}
        />
      )}
    </div>
  )
}
